/**
 * uscfValidationStatus.ts - Renders the inline status line under a
 * player card's USCF ID field (rating + membership status, or a
 * pending/error state). Shared by:
 *   - uscfSearch.ts: renders immediately from modal search result data
 *     (no extra network call - the search result already has what we need)
 *   - the blur/Next-triggered lookup flow (once that endpoint exists):
 *     will call the same render functions from its own fetch response
 *
 * Markup expected inside each .player-card, directly under the USCF ID
 * field:
 *   <div class="js-uscf-status form-text"></div>
 */

export interface UscfStatusData {
    rating: number | null;
    isActive: boolean;
    expirationDate: string | null;
}

function getStatusEl(card: HTMLElement): HTMLElement | null {
    return card.querySelector<HTMLElement>('.js-uscf-status');
}

export function renderUscfStatusIdle(card: HTMLElement): void {
    const el = getStatusEl(card);
    if (!el) return;
    el.className = 'js-uscf-status form-text';
    el.innerHTML = '';
}

export function renderUscfStatusPending(card: HTMLElement): void {
    const el = getStatusEl(card);
    if (!el) return;
    el.className = 'js-uscf-status form-text text-muted';
    el.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Checking USCF ID...';
}

export function renderUscfStatusSuccess(card: HTMLElement, data: UscfStatusData): void {
    const el = getStatusEl(card);
    if (!el) return;

    const ratingText = data.rating ? `Rating: ${data.rating}` : 'Unrated';

    if (data.isActive) {
        el.className = 'js-uscf-status form-text text-success';
        el.innerHTML = `&#10003; Active &middot; ${ratingText}`;
    } else {
        const expiredText = data.expirationDate
            ? `Membership expired ${data.expirationDate}`
            : 'Membership expired';
        el.className = 'js-uscf-status form-text text-warning-emphasis';
        el.innerHTML = `&#9888; ${expiredText} &middot; ${ratingText}`;
    }
}

export function renderUscfStatusNotFound(card: HTMLElement): void {
    const el = getStatusEl(card);
    if (!el) return;
    el.className = 'js-uscf-status form-text text-danger';
    el.innerHTML = 'USCF ID not found &mdash; double check the number, or ' +
        '<a href="#" class="js-btn-open-uscf-search">search by name instead</a>.';
}

export function renderUscfStatusError(card: HTMLElement): void {
    const el = getStatusEl(card);
    if (!el) return;
    el.className = 'js-uscf-status form-text text-muted';
    el.innerHTML = "Couldn't verify right now &mdash; you can continue and we'll re-check before payment.";
}